import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ZAI_CHAT_FILE_COUNT_REFETCH_EVENT,
  ZAI_CHAT_MODAL_INFO_OPEN_EVENT,
  ZAI_CHAT_MODAL_UPLOAD_OPEN_EVENT,
} from '@winnin/zai-chat'

import { useCurrentLanguage } from 'lib/tolgee'
import { zaiUtils } from 'modules/zai/common/zai.utils'
import { FeatureFlagsStore, PanelStore } from 'stores'
import { analyticsTracker } from 'utils'

import { ZaiChatObserverContext } from './context'
import type {
  IZaiChatMessage,
  IZaiChatObserverContextData,
  IZaiChatObserverProviderProps,
  TZaiChatActions,
} from './types'

export function ZaiChatObserverProvider({ children }: IZaiChatObserverProviderProps) {
  const language = useCurrentLanguage()
  const workspaceId = Object(PanelStore?.selectedPanel)?.panelId
  const zaiChatIframeRef = useRef<HTMLIFrameElement | null>(null)

  const isNativeZaiChatEnabled = FeatureFlagsStore.getFeatureFlag('isNativeZaiChatEnabled')

  const [modalCallbacks, setModalCallbacks] = useState<{
    onModalDocumentsOpen?: () => void
    onModalDocumentsTipsOpen?: () => void
  }>({})

  const registerModalCallbacks = useCallback(
    (callbacks: { onModalDocumentsOpen?: () => void; onModalDocumentsTipsOpen?: () => void }) => {
      setModalCallbacks(callbacks)
    },
    []
  )

  const iframeSrc = useMemo(
    () =>
      zaiUtils.generateZaiChatSrc({
        module: 'center-stage',
        workspaceId,
        language,
      }),
    [workspaceId, language]
  )

  const onModalDocumentsOpenWithTracking = useCallback(() => {
    if (!modalCallbacks.onModalDocumentsOpen) return

    const { FIL_OPEN_MODAL } = analyticsTracker.eventNames

    analyticsTracker.eventTrack(FIL_OPEN_MODAL)

    modalCallbacks.onModalDocumentsOpen()
  }, [modalCallbacks])

  const actions: { [key in Exclude<TZaiChatActions, 'file-count-refetch'>]: () => void } = useMemo(
    () => ({
      'modal-upload-open': onModalDocumentsOpenWithTracking || (() => {}),
      'modal-info-open': modalCallbacks.onModalDocumentsTipsOpen || (() => {}),
    }),
    [onModalDocumentsOpenWithTracking, modalCallbacks.onModalDocumentsTipsOpen]
  )

  const postMessageToZaiChat = useCallback(
    (action: Extract<TZaiChatActions, 'file-count-refetch'>) => {
      if (!zaiChatIframeRef.current?.contentWindow) return

      const message: IZaiChatMessage = {
        origin: 'insights',
        action,
      }

      zaiChatIframeRef.current.contentWindow.postMessage(message, iframeSrc)
    },
    [iframeSrc]
  )

  const triggerFileCountRefetch = useCallback(() => {
    if (isNativeZaiChatEnabled) {
      window.dispatchEvent(new CustomEvent(ZAI_CHAT_FILE_COUNT_REFETCH_EVENT))

      return
    }

    postMessageToZaiChat('file-count-refetch')
  }, [isNativeZaiChatEnabled, postMessageToZaiChat])

  useEffect(() => {
    if (isNativeZaiChatEnabled) {
      window.addEventListener(ZAI_CHAT_MODAL_UPLOAD_OPEN_EVENT, actions['modal-upload-open'])
      window.addEventListener(ZAI_CHAT_MODAL_INFO_OPEN_EVENT, actions['modal-info-open'])

      return () => {
        window.removeEventListener(ZAI_CHAT_MODAL_UPLOAD_OPEN_EVENT, actions['modal-upload-open'])
        window.removeEventListener(ZAI_CHAT_MODAL_INFO_OPEN_EVENT, actions['modal-info-open'])
      }
    }

    // TODO: Delete from here down to the end of useEffect after isNativeZaiChatEnabled is enabled for all users
    const handleMessage = (event: MessageEvent) => {
      const message = event.data as Exclude<IZaiChatMessage, 'file-count-refetch'>

      const eventOriginObjectUrl = new URL(event.origin)
      const eventOrigin = eventOriginObjectUrl.origin

      const iframeOriginObjectUrl = new URL(iframeSrc)
      const iframeOrigin = iframeOriginObjectUrl.origin

      const action = actions[message?.action]

      if (!action || eventOrigin !== iframeOrigin || message?.origin !== 'zai-chat') return

      action()
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, isNativeZaiChatEnabled])

  const contextValue: IZaiChatObserverContextData = useMemo(
    () => ({
      triggerFileCountRefetch,
      zaiChatIframeRef,
      iframeSrc,
      registerModalCallbacks,
    }),
    [triggerFileCountRefetch, iframeSrc, registerModalCallbacks]
  )

  return <ZaiChatObserverContext.Provider value={contextValue}>{children}</ZaiChatObserverContext.Provider>
}
